import { Dispatch, SetStateAction, useMemo, useCallback, useEffect } from 'react';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import {
  selectedRiskAssessmentFilters,
  riskAssessmentAssemblyTemplate,
  maintainSelectedEquipments,
} from '@/components/Analysis/state';
import useDialogState from '../hooks/useDialogState';
import { useFiltersForRisk } from '../hooks/useFiltersForRisk';
import { FilterOption, FilterType } from '@/types';
import { FilterChip } from '@/components/shared/FilterAndSearchPanel/styles';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Checkbox, Tooltip } from '@mui/material';
import { abyssColors } from '@/theme/colors';

type RiskAssessmentFiltersProps = {
  onStructureTypeSelected?: Dispatch<SetStateAction<boolean>>;
  isDisabled: boolean;
  filtersOrientation: 'row' | 'column';
  isStructureTypeSelected?: boolean;
};

const defaultFilterValue = null;

const defaultFilters = new Set([
  'risk_level_1_component',
  'risk_level_2_component',
  'risk_damage_class',
]);

export const RiskAssessmentFilters = ({
  onStructureTypeSelected,
  isDisabled,
  filtersOrientation,
  isStructureTypeSelected,
}: RiskAssessmentFiltersProps) => {
  const template = useAtomValue(riskAssessmentAssemblyTemplate);
  const [selectedFilters, setSelectedFilters] = useAtom(selectedRiskAssessmentFilters);
  const setMaintainSelections = useSetAtom(maintainSelectedEquipments);
  const { resetDialogState } = useDialogState();

  const { filters } = useFiltersForRisk({ source: 'calculations', selectedFilters });

  useEffect(() => {
    if (onStructureTypeSelected) {
      onStructureTypeSelected(
        selectedFilters.some((filter) => filter.id === 'risk_level_2_component')
      );
    }
  }, [onStructureTypeSelected, selectedFilters, template?.fields]);

  const handleFilterChange = useCallback(
    (values: FilterOption | FilterOption[] | null, filterKey: string) => {
      setMaintainSelections(false);

      setSelectedFilters((previousSelectedFilters) => {
        const normalizedValues = Array.isArray(values) ? values : values ? [values] : [];
        if (filterKey === 'risk_level_2_component' && normalizedValues.length === 0) {
          return previousSelectedFilters.filter((filter) => filter.id === 'risk_level_1_component');
        }
        const updatedFilters = previousSelectedFilters.filter((filter) => filter.id !== filterKey);
        if (normalizedValues.length === 0) return updatedFilters;
        const newFilters = normalizedValues.map(({ label }) => ({ id: filterKey, value: label }));
        return [...updatedFilters, ...newFilters];
      });

      resetDialogState();
    },
    [setMaintainSelections, resetDialogState, setSelectedFilters]
  );

  const filteredFilters = useMemo(
    () => filters.filter(({ key }) => isStructureTypeSelected || defaultFilters.has(key)),
    [filters, isStructureTypeSelected]
  );

  if (!template) {
    return (
      <Skeleton
        data-testid="risk-filters-skeleton"
        variant="rectangular"
        height={200}
        width="100%"
      />
    );
  }

  return (
    <Stack spacing={1} direction={filtersOrientation} sx={{ width: '100%', alignItems: 'center' }}>
      {filteredFilters.map(({ type, key, label, options }) => {
        const isMultiSelect = type === FilterType.MULTI_SELECT;
        const findValue = selectedFilters.filter((f) => f.id === key);
        let selectedValue;
        if (isMultiSelect) selectedValue = findValue.map((f) => ({ key: f.value, label: f.value }));
        else if (findValue.length > 0)
          selectedValue = { key: findValue[0].value, label: findValue[0].value };
        else selectedValue = defaultFilterValue;

        return (
          <Autocomplete
            multiple={isMultiSelect}
            disableCloseOnSelect={isMultiSelect}
            fullWidth
            key={key}
            options={options}
            autoComplete
            includeInputInList
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            noOptionsText="No Options"
            renderInput={(parameters) => (
              <TextField
                {...parameters}
                label={label}
                variant="standard"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
            onChange={(_, value) => handleFilterChange(value, key)}
            disabled={isDisabled}
            value={selectedValue}
            renderTags={(value, getTagProps) => {
              const tagsLength = value.length;
              const limitTags = 2;
              const tooltipTitle = value
                .map((item) => item.label)
                .slice(limitTags)
                .join(', ');

              return (
                <>
                  {value.slice(0, limitTags).map((option, index) => (
                    <Tooltip key={option.key} arrow title={option.label} placement="top">
                      <FilterChip
                        {...getTagProps({ index })}
                        key={option.key}
                        label={option.label}
                        size="small"
                        sx={{
                          backgroundColor: abyssColors.primary[100],
                          color: abyssColors.primary[500],
                          mb: '0.5rem',
                          width: 'fit-content',
                          marginY: '0.3rem',
                        }}
                      />
                    </Tooltip>
                  ))}
                  {tagsLength > limitTags && (
                    <Tooltip arrow title={tooltipTitle} placement="top">
                      <FilterChip
                        sx={{
                          backgroundColor: abyssColors.primary[100],
                          color: abyssColors.primary[500],
                          mb: '0.5rem',
                          width: 'fit-content',
                          marginY: '0.3rem',
                        }}
                        label={`+${tagsLength - limitTags}`}
                        size="small"
                      />
                    </Tooltip>
                  )}
                </>
              );
            }}
            renderOption={(props, option, { selected }) => (
              <li style={{ padding: '0px 8px', minHeight: 30 }} {...props}>
                {isMultiSelect && (
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" color="info" />}
                    sx={{ marginRight: 1 }}
                    checked={selected}
                  />
                )}
                {option.label}
              </li>
            )}
            sx={{
              '& .MuiChip-root': {
                marginRight: '8px',
                marginLeft: '-4px',
              },
              '& fieldset': {
                border: 'none',
              },
            }}
          />
        );
      })}
    </Stack>
  );
};
